<!--
  MsaGateBypassCard -- the SA "Agreement launch gate" section of the
  distributor page (DistributorEdit.vue).

  Implements docs/planning/2026-10-19-distributor-setup-ia-proposal.md
  decision F2 (Jake, 2026-10-21): a store can go Live only after the
  distributor's Owner confirms the CURRENT Master Services Agreement.
  Platform staff may waive that ONE gate for THIS distributor (for example a
  paper agreement signed outside the app). Off by default. Turning it on or
  off REQUIRES a reason and an explicit confirmation; the server writes an
  audit row for each change. The other four launch gates are unaffected.

  Admin app only: the endpoint answers every non-staff caller with 404, and
  no tenant payload carries the record. Tenant-facing copy and docs never
  mention it.

  Its own explicit action, not part of the page's auto-saving profile form:
  the change takes effect the moment it is confirmed.
-->
<template>
    <section class="msa-gate" data-testid="sa-msa-gate-bypass-card">
        <h2 class="msa-gate__title">Agreement launch gate</h2>
        <p class="msa-gate__help">
            A store can go live only after an account Owner confirms the
            current Master Services Agreement. Bypass this one check for this
            distributor only when the agreement is settled another way. The
            other launch checks still apply.
        </p>

        <p v-if="loading" data-testid="sa-msa-gate-bypass-loading">Loading...</p>

        <p
            v-else-if="loadError"
            class="msa-gate__error"
            data-testid="sa-msa-gate-bypass-load-error"
        >
            {{ loadError }}
        </p>

        <template v-else>
            <p
                class="msa-gate__state"
                data-testid="sa-msa-gate-bypass-state"
                :data-bypassed="state.bypassed ? 'true' : 'false'"
            >
                <template v-if="state.bypassed">
                    Bypassed since {{ formatDate(state.bypassed_at) }}<template
                        v-if="state.bypassed_by"
                    > by {{ state.bypassed_by.name }}</template>: {{ state.reason }}
                </template>
                <template v-else>Enforced (not bypassed)</template>
            </p>

            <p class="msa-gate__msa" data-testid="sa-msa-gate-bypass-msa-status">
                <template v-if="state.msa_confirmed">
                    The Owner has confirmed the current agreement, so this
                    distributor does not need the bypass.
                </template>
                <template v-else>
                    The Owner has not confirmed the current agreement.
                </template>
            </p>

            <form
                class="msa-gate__form"
                data-testid="sa-msa-gate-bypass-form"
                @submit.prevent="ask"
            >
                <label class="msa-gate__label" for="sa-msa-gate-bypass-reason">
                    Reason (required)
                </label>
                <input
                    id="sa-msa-gate-bypass-reason"
                    v-model="reason"
                    type="text"
                    maxlength="500"
                    class="msa-gate__input"
                    data-testid="sa-msa-gate-bypass-reason"
                />
                <button
                    type="button"
                    class="msa-gate__btn"
                    data-testid="sa-msa-gate-bypass-submit"
                    :disabled="!reasonValid || saving"
                    @click="ask"
                >
                    {{ state.bypassed ? "Enforce the gate again" : "Bypass the gate" }}
                </button>
            </form>

            <!-- Explicit confirmation before the write. Cancel keeps the reason. -->
            <div
                v-if="confirming"
                class="msa-gate__confirm"
                role="alertdialog"
                data-testid="sa-msa-gate-bypass-confirm"
            >
                <p>
                    <template v-if="state.bypassed">
                        Enforce the agreement gate again? New stores for this
                        distributor cannot go live until the Owner confirms the
                        current agreement.
                    </template>
                    <template v-else>
                        Bypass the agreement gate? This distributor's stores can
                        go live without the Owner confirming the current
                        agreement.
                    </template>
                    Reason: {{ reason.trim() }}
                </p>
                <button
                    type="button"
                    class="msa-gate__btn"
                    data-testid="sa-msa-gate-bypass-confirm-submit"
                    :disabled="saving"
                    @click="onConfirm"
                >
                    Confirm
                </button>
                <button
                    type="button"
                    class="msa-gate__btn"
                    data-testid="sa-msa-gate-bypass-confirm-cancel"
                    @click="confirming = false"
                >
                    Cancel
                </button>
            </div>

            <p
                v-if="saveError"
                class="msa-gate__error"
                data-testid="sa-msa-gate-bypass-error"
            >
                {{ saveError }}
            </p>
        </template>
    </section>
</template>

<script>
import { getMsaGateBypass, setMsaGateBypass } from "@/services/superadminMsaGateBypassService";

const REASON_MAX = 500;

const EMPTY = {
    bypassed: false,
    bypassed_at: null,
    reason: null,
    bypassed_by: null,
    msa_confirmed: false,
};

function serverMessage(e, fallback) {
    return (e && e.response && e.response.data && e.response.data.message) || fallback;
}

export default {
    name: "MsaGateBypassCard",

    props: {
        distributorId: { type: [String, Number], required: true },
    },

    data() {
        return {
            loading: true,
            loadError: "",
            saving: false,
            saveError: "",
            confirming: false,
            state: { ...EMPTY },
            reason: "",
        };
    },

    computed: {
        reasonValid() {
            const n = this.reason.trim().length;
            return n >= 1 && n <= REASON_MAX;
        },
    },

    watch: {
        distributorId() {
            this.load();
        },
    },

    mounted() {
        this.load();
    },

    methods: {
        normalize(resp) {
            const data = resp && resp.data ? resp.data : resp;
            return { ...EMPTY, ...(data || {}) };
        },

        async load() {
            this.loading = true;
            this.loadError = "";
            try {
                this.state = this.normalize(await getMsaGateBypass(this.distributorId));
            } catch (e) {
                this.loadError = serverMessage(e, "Could not load the agreement gate setting.");
            } finally {
                this.loading = false;
            }
        },

        // Every path in (click, Enter, form submit) only OPENS the
        // confirmation; the write happens solely from Confirm.
        ask() {
            if (this.reasonValid && !this.saving) this.confirming = true;
        },

        async onConfirm() {
            this.confirming = false;
            if (!this.reasonValid || this.saving) return;
            this.saving = true;
            this.saveError = "";
            try {
                const resp = await setMsaGateBypass(
                    this.distributorId,
                    !this.state.bypassed,
                    this.reason.trim(),
                );
                // Re-render from the server's record, never a local guess.
                this.state = this.normalize(resp);
                this.reason = "";
            } catch (e) {
                this.saveError = serverMessage(e, "Could not change the agreement gate setting.");
            } finally {
                this.saving = false;
            }
        },

        formatDate(iso) {
            if (!iso) return "";
            const d = new Date(iso);
            return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleDateString();
        },
    },
};
</script>

<style scoped>
.msa-gate {
    margin-top: 24px;
}
.msa-gate__title {
    font-size: 1.1rem;
    margin-bottom: 8px;
}
.msa-gate__help,
.msa-gate__msa {
    font-size: 0.9rem;
    opacity: 0.85;
    margin-bottom: 8px;
}
.msa-gate__state {
    font-weight: 600;
    margin-bottom: 8px;
}
.msa-gate__form {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
}
.msa-gate__input {
    flex: 1 1 280px;
    min-width: 200px;
    border-bottom: 1px solid currentColor;
    padding: 4px 2px;
}
.msa-gate__btn {
    padding: 6px 14px;
    border: 1px solid currentColor;
    border-radius: 4px;
}
.msa-gate__btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}
.msa-gate__confirm {
    margin-top: 12px;
    padding: 8px 12px;
    border: 1px solid currentColor;
    border-radius: 4px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
}
.msa-gate__error {
    color: #b00020;
    margin-top: 8px;
}
</style>
